/**
 * CUA error handlers — CUA-specific API errors (blocked_app, credential_detected, rate_limit)
 * Reference: TASK-UI-CU-005-2, SOL-UI-CU-005
 *
 * Usage: call handleCUAError(error) BEFORE generic error handler in afterResponse/beforeError hooks.
 */

import { toast } from '@/shared/stores/notificationStore';
import { mapCUAError } from '@/features/computer-use/lib/errorMessages';
import { cuaConsentStore } from '@/features/computer-use/hooks/useCUAConsent';

export interface CUAAPIErrorBody {
  code?: string;
  detail?: string;
  details?: Record<string, unknown>;
}

/**
 * Handles CUA-specific HTTP errors.
 * Returns true if the error was handled (caller should not show generic error).
 * Returns false if not a CUA error — caller should proceed with normal handling.
 */
export function handleCUAError(status: number, body: CUAAPIErrorBody): boolean {
  const code = body?.code ?? '';
  const details = body?.details ?? {};

  // 403 — connector not granted → show consent modal
  if (status === 403 && code === 'connector_not_granted') {
    cuaConsentStore.getState().showModal();
    return true;
  }

  // 422 — blocked app
  if (status === 422 && code === 'blocked_app') {
    const message = mapCUAError(code, details);
    toast.warning('Ứng dụng bị chặn', message);
    return true;
  }

  // 422 — credential detection
  if (status === 422 && code === 'credential_detected') {
    const message = mapCUAError(code, details);
    toast.error('Thao tác bị chặn', message);
    return true;
  }

  // 429 — rate limit (CUA-specific)
  if (status === 429 && code === 'cua_rate_limit') {
    const message = mapCUAError(code, details);
    toast.warning('Giới hạn tốc độ CUA', message);
    return true;
  }

  // 429 — quota exceeded
  if (status === 429 && code === 'cua_quota_exceeded') {
    const message = mapCUAError(code, details);
    toast.warning('Vượt quá giới hạn CUA', message);
    return true;
  }

  return false;
}
